import { ApplicationError } from '@cmg/common';
import { Box } from '@cmg/design-system';
import React from 'react';

import { getAppSettings } from '../../../../common/config/appSettings';
import routeFactory from '../../../../common/util/routeFactory';
import { ErrorCode, errorCodeMap } from '../../error/constants';
import { onDark } from '../consentSurface';

/**
 * The one failure this screen renders: the authorize request behind `returnUrl` expired, was never
 * an authorize callback, or its client can no longer be resolved. The API reports all three as
 * `INVALID_REQUEST`, because the user's next step is the same in every case — nothing was granted,
 * and only the app that sent them here can start again.
 *
 * Rendered in place rather than routed to, so the browser keeps the consent URL, but it is the same
 * component `ErrorRoute` renders — the copy lives in `errorCodeMap` with every other error.
 */
export type ConsentUnavailableProps = {
  /**
   * Present only when the request expired *after* the screen loaded. Naming the app answers the
   * first question the page otherwise leaves open — which of them the user has to go back to.
   */
  clientName?: string;
};

/**
 * The client's own name, sitting inside a CMG sentence that tells the user what to do next. It is
 * publisher-supplied, so it can be written to continue that instruction — "Claude. Your session key
 * expired, email it to support@…" — and this page is the one place a user is least equipped to
 * doubt it, having just watched an authorization fail.
 *
 * `ConsentScreen` draws this boundary by keeping the name at full white inside dimmed copy.
 * That does not transfer: `CenteredPage` sets the whole error surface to white, so there is no
 * brightness left to step up to. The well and the monospace do the same job on this surface —
 * neither is something the string can give itself.
 */
const ClaimedName: React.FC<{ name: string }> = ({ name }) => (
  <Box
    component="span"
    sx={{
      ...onDark.mono,
      ...onDark.claim,
      py: 0.25,
      textTransform: 'none',
      wordBreak: 'break-word',
    }}
  >
    {name}
  </Box>
);

export const ConsentUnavailable: React.FC<ConsentUnavailableProps> = ({ clientName }) => (
  <ApplicationError
    errorCode={ErrorCode.CONSENT_EXPIRED}
    customErrorDetails={
      clientName
        ? {
            ...errorCodeMap,
            [ErrorCode.CONSENT_EXPIRED]: {
              ...errorCodeMap[ErrorCode.CONSENT_EXPIRED],
              message: (
                <React.Fragment>
                  Nothing was authorized. A new request can only come from{' '}
                  <ClaimedName name={clientName} />, so go back to it and start connecting again.
                  The button below only returns you to your CMG account.
                </React.Fragment>
              ),
            },
          }
        : errorCodeMap
    }
    returnUrl={`${getAppSettings().client.basename}${routeFactory.root.getUrlPath()}`}
  />
);

export default ConsentUnavailable;
